import Logo from "~/components/Logo"
import {
  accompanyingPrevious,
  articleBodyStyle,
  articleCompareImages,
  articleFaqBodyStyle,
  articleImageStyle,
  bold,
  faqGrid,
  faqH3Style,
  figCaptionStyle,
  h1Style,
  h1Wrap,
  h2Style,
  h3Style,
  headerTextStyle,
  stepSectionStyle,
  unorderedList,
} from "./article.css"
import { MetaFunction } from "@remix-run/node"
import StickyHeader from "~/components/StickyHeader"
import { page } from "~/shared/styles.css"
import LinkButton from "~/components/LinkButton"
import Link from "~/components/Link"
import Footer from "~/components/Footer"
import { golden, vars } from "~/components/styles/theme.css"
import { Bold, H2, H3, P, Section } from "~/components/articleComponents"
import ScrollyWolly from "~/components/ScrollyWolly"

const TITLE = "Urban Gardens - Materials and Plants | Tips for Urban Spaces"
// SEO FIX: Description shortened from 185 to 152 chars (was over 155 char limit)
const DESCRIPTION =
  "Discover the best materials and plants for urban gardens. Use containers, vertical structures, and clever planting to transform small city spaces into green havens."
const URL = "https://rescapeai.com/garden-design-guide/urban/materials-and-plants"
const IMAGE = "https://rescapeai.com/guide/urban-garden-1.webp"

export const meta: MetaFunction = () => {
  return [
    {
      title: TITLE,
    },
    {
      name: "description",
      content: DESCRIPTION,
    },
    {
      property: "og:title",
      content: TITLE,
    },
    {
      property: "og:description",
      content: DESCRIPTION,
    },
    {
      property: "og:image",
      content: IMAGE,
    },
    // SEO FIX: Added Twitter Card tags
    {
      name: "twitter:card",
      content: "summary_large_image",
    },
    {
      name: "twitter:title",
      content: TITLE,
    },
    {
      name: "twitter:description",
      content: DESCRIPTION,
    },
    {
      name: "twitter:image",
      content: IMAGE,
    },
    {
      tagName: "link",
      rel: "canonical",
      href: URL,
    },
    {
      "script:ld+json": {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: TITLE,
        description: DESCRIPTION,
        author: {
          "@type": "Organization",
          name: "Rescape AI",
        },
        datePublished: "2025-10-22",
        dateModified: "2025-10-22",
        publisher: {
          "@type": "Organization",
          name: "Rescape AI",
          logo: {
            "@type": "ImageObject",
            url: "https://rescapeai.com/logo.webp",
          },
        },
        image: IMAGE,
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id": URL,
        },
      },
    },
  ]
}

function Index() {
  return (
    <>
      <div
        className={h1Style}
        style={{
          textAlign: "center",
          backgroundImage: "url(/guide/urban-garden-1.webp)",
          backgroundPositionY: "80%",
        }}
      >
        <div className={h1Wrap}>
          <Logo width="108px" />
          <h1 className={headerTextStyle}>
            Urban Gardens: A Guide to Materials, Plants, and City Green Spaces
          </h1>
        </div>
      </div>
      <StickyHeader status="SignUp" />

      <div className={page}>
        <section className={stepSectionStyle}>
          <P>
            Urban gardens turn small, often overlooked city plots, balconies or backyards
            into living, breathing green spaces. They combine function and beauty,
            offering fresh produce, biodiversity, and a peaceful retreat from the bustle
            of city life — even within the hardest-to-garden surroundings.
          </P>
          <H2>Design Flow and Structure — What Works in Urban Gardens</H2>
          <P>
            Urban gardens thrive when carefully structured to suit their constrained
            setting. Unlike sprawling country plots, city gardens often require more
            deliberate planning. You might be working with a narrow backyard, a courtyard
            between buildings, or a small balcony. With that in mind:
          </P>
          <figure>
            <ScrollyWolly
              height={`${golden * 10}rem`}
              yPosition="50%"
              marginTop="1rem"
              before={{
                image: "/guide/raised-planter.jpg",
                title: "Raised Containers",
                alt: "Raised Containers in Urban Garden",
              }}
              after={{
                image: "/guide/raised-planter-2.jpg",
                title: "",
                alt: "Raised plant containers",
              }}
            />
            <figcaption className={figCaptionStyle}>
              Examples of raised planters in urban gardens
              <br />
              Design urban gardens on <Link href="/hello/signup">Rescape AI</Link>
            </figcaption>
          </figure>
          <ul className={unorderedList}>
            <li>
              <Bold>Raised beds, containers, and pots</Bold> — Especially useful when the
              soil quality is poor, or when you're working on a balcony or paved
              courtyard. This lets you bring in suitable soil and control drainage.
            </li>
            <li>
              <Bold>Paths, paving, and decking</Bold> — Use materials that complement the
              garden's design aesthetic. Consider smaller paving stones, gravel, or
              lightly coloured surfaces to keep the space feeling open and bright. Raised
              or inset decking can help define a seating or dining area in a tight space.
            </li>
            <li>
              <Bold>Vertical structures and supports</Bold> — Trellises, walls, fences,
              pergolas, or railings give support to climbing plants and make use of
              vertical space — a key advantage when ground space is limited.
            </li>
            <li>
              <Bold>Simple and cohesive material palette</Bold> — Using a limited range of
              materials (e.g. one type of paving, one type of wood or metal for
              containers/fences) helps create a unified look. As in one well-designed
              urban garden, crushed limestone, cedar, and steel cladding were combined to
              give coherence.
            </li>
            <li>
              <Bold>Containers, pots, planters</Bold> — Especially for balconies or
              patios, containers let you grow herbs, edibles or ornamentals even without
              ground soil. Use a mix of pot sizes to introduce scale and variety.
            </li>
          </ul>
        </section>
        <Section>
          <H2>Softscape (Plants, Soil, and Living Materials)</H2>
          <P>
            Soft landscaping — the plants and living elements — brings an urban garden
            alive. Because city gardens often face limitations (soil quality, light,
            drainage, space), choosing the right kinds of plants is vital.
          </P>
          <img
            style={{ maxHeight: "360px", objectFit: "cover", marginTop: "0.5rem" }}
            className={articleImageStyle}
            src="/guide/urban-garden-softscape.jpg"
            alt="Softscape planting in an urban garden with mixed perennials and shrubs"
          />
          <ul className={unorderedList}>
            <li>
              <Bold>Improved soil or compost in containers or beds</Bold> — Urban soils
              may be compacted, poor quality, or contaminated. Using high-quality potting
              soil or compost in raised beds/containers ensures good root growth.
            </li>
            <li>
              <Bold>Mix of plants for structure, texture, and interest</Bold> — Structural
              plants (shrubs, small trees, clipped forms) create permanent shape and
              backbone. Grasses, perennials, seasonal flowers, herbs and edibles add
              seasonal interest, texture, colour and fragrance.
            </li>
            <li>
              <Bold>Shade- and sun-adapted plants</Bold> — Depending on the light exposure
              of your garden, choose accordingly: shade-loving plants for dark corners,
              sun-loving for brighter spots.
            </li>
            <li>
              <Bold>Vertical planting and climbers</Bold> — On walls, fences or trellises,
              climbing plants make the most of vertical space while adding greenery and
              softening built features. This is especially important in small urban
              gardens.
            </li>
            <li>
              <Bold>Edibles, herbs and practical planting</Bold> — Herbs and small
              vegetables are ideal for urban gardens — they don't need a lot of space and
              reward you with fresh produce.
            </li>
          </ul>
        </Section>
        <Section>
          <H2>Materials for Urban Gardens – Hardscape & Softscape</H2>
          <P>
            <Bold>Hardscape & Structural Materials</Bold>
          </P>
          <P>
            These features provide the "bones" of your garden — walkways, edges, planting
            containers, fences, walls, seating areas, and vertical supports for climbing
            plants. In urban gardens, materials should be chosen to maximize functionality
            while also enhancing aesthetics. Raised beds, containers, and pots are
            especially useful when soil quality is poor or when working on a balcony or
            paved courtyard, as they allow you to bring in suitable soil and control
            drainage.
          </P>
          <P>
            Paths, paving, and decking should use materials that complement the garden's
            design aesthetic; smaller paving stones, gravel, or lightly coloured surfaces
            help keep the space feeling open and bright, while raised or inset decking can
            define seating or dining areas.{" "}
          </P>
          <P>
            Vertical structures and supports such as trellises, walls, fences, pergolas,
            or railings provide space for climbing plants and help make the most of
            limited ground area. Using a simple, cohesive material palette — for example,
            one type of paving and one type of wood or metal — also helps create unity;
            one well-designed urban garden used crushed limestone, cedar, and steel
            cladding for a harmonious look.
          </P>
          <P>
            Containers, pots, and planters are particularly valuable for balconies or
            patios, allowing you to grow herbs, edibles, or ornamentals even without
            access to ground soil, and a mix of pot sizes adds scale and visual interest.
          </P>
        </Section>
        <Section>
          <H2>Soft landscaping</H2>
          <P>
            <Bold>The plants and living elements — brings an urban garden to life. </Bold>
          </P>
          <img
            style={{ maxHeight: "360px", objectFit: "cover", marginTop: "0.5rem" }}
            className={articleImageStyle}
            src="/guide/small-container-urban.jpg"
            alt="Small containers with herbs and flowers on a city balcony or patio"
          />
          <P>
            City gardens often face limitations such as poor soil quality, restricted
            light, drainage issues, or a lack of space, so selecting the right types of
            plants is essential.
          </P>
          <P>
            Using improved soil or compost in containers or raised beds is often necessary
            because many urban soils are compacted, low in nutrients, or contaminated;
            high-quality potting mixes encourage strong root growth. A thoughtful mix of
            plants provides structure, texture, and seasonal interest: shrubs, small
            trees, and clipped forms create a permanent backbone, while grasses,
            perennials, herbs, and edibles add colour, fragrance, and movement.
          </P>
          <P>
            It is also important to choose plants suited to the garden's light conditions,
            selecting shade-loving varieties for darker areas and sun-loving ones for
            brighter spots. Vertical planting and climbers take advantage of walls,
            fences, or trellises, maximizing vertical space and softening hard surfaces —
            a key strategy for small urban gardens.
          </P>
          <P>
            Edibles and herbs are especially practical, as they require little space and
            reward the gardener with useful, fresh produce.
          </P>
          <P>
            Because urban gardens often deal with space, light, and soil constraints,
            certain plants tend to perform particularly well. Ferns, hostas, and other
            shade-loving perennials thrive in shady courtyards or north-facing gardens,
            bringing greenery to darker corners.
          </P>
          <P>
            Ornamental grasses and flowing perennials add movement, texture, and softness,
            especially when paired with more structured plants. Small shrubs and
            structural plants provide year-round shape and definition, forming the
            backbone of a coherent design.
          </P>
          <P>
            Herbs and compact edibles such as basil, thyme, salad greens, or cherry
            tomatoes grow very well in containers or small beds, making them ideal choices
            for balconies or compact gardens. Climbers and other vertical plants use
            walls, fences, or trellises to create lush, green boundaries and maximize
            limited space.
          </P>
        </Section>
        <Section>
          <H2>Combining plants with different growth habits</H2>
          <img
            style={{ maxHeight: "360px", objectFit: "cover", marginTop: "0.5rem" }}
            className={articleImageStyle}
            src="/guide/steel-container-urban.jpg"
            alt="Steel containers with mixed plants showing varied growth habits in urban garden"
          />
          <P>
            Urban gardens offer benefits far beyond beauty. They can improve air quality,
            enhance biodiversity, and strengthen climate resilience by adding green
            pockets to built-up areas, filtering pollutants, sequestering CO₂, and
            providing habitat for wildlife.
          </P>
          <P>
            They also help mitigate heat by cooling the surrounding area, reducing the
            "urban heat island" effect through shading and natural moisture release.
          </P>
          <P>
            Edible planting supports fresh, home-grown produce, offering economic and
            nutritional value to city dwellers.
          </P>
          <P>
            On a personal level, gardens support mental health and wellbeing by offering
            privacy, calm, and connection to nature; gardening itself is grounding,
            relaxing, and emotionally rewarding.
          </P>
          <P>
            To keep an urban garden functional and attractive year-round, it helps to plan
            for all seasons by combining evergreens, perennials, shrubs, annuals, and
            herbs to ensure consistent interest. Containers and raised beds offer
            flexibility, better soil control, and easy rearrangement — useful for renters
            or anyone who likes to adapt their layout. Making use of vertical space with
            trellises, wall-mounted planters, or green walls increases planting
            opportunities in tight areas.
          </P>
          <P>
            It's also important to consider local conditions such as light, wind, soil,
            and drainage: shade-tolerant plants for dim courtyards, hardy containers for
            windy terraces, high-quality potting soil where ground conditions are poor,
            and good drainage in paved or compacted areas. Keeping maintenance manageable
            is key in small gardens; too many species or overly complex planting schemes
            can increase upkeep. A restrained, well-chosen palette repeated in generous
            groupings typically looks cohesive and is easier to manage.
          </P>
        </Section>
        <Section>
          <H2>Conclusion — Urban Gardens: Small Spaces, Big Potential</H2>
          <P>
            Urban gardens may be modest in size — but with the right design approach,
            materials and plants, they can become verdant, useful, and deeply rewarding
            parts of city living. By combining structural materials (beds, containers,
            paving, vertical supports) with soft landscaping (plants selected for light,
            space, purpose), you can create a garden that brings biodiversity, fresh herbs
            or vegetables, seasonal beauty, and calm to even the most compact urban plot.
          </P>
          <P>
            Whether you have a narrow back yard, a rooftop terrace, a balcony or even just
            a paved courtyard — thoughtful design and plant choices can help you build a
            green oasis that enriches both your home and your city environment.
          </P>
        </Section>
        <section className={stepSectionStyle}>
          <h2 className={h2Style}>FAQ's</h2>

          <div className={faqGrid}>
            <div>
              <h3 className={faqH3Style}>
                What makes urban garden design different from regular garden design?
              </h3>
              <p
                className={articleFaqBodyStyle}
                style={{
                  color: vars.color.light3,
                  opacity: 0.8,
                }}
              >
                Urban garden design must work within tighter constraints — limited space,
                poor soil, shade from surrounding buildings, and sometimes wind exposure
                on balconies or rooftops. Because of these factors, urban gardens rely
                heavily on containers, vertical space, and carefully selected plants that
                thrive in constrained environments.
              </p>
            </div>
            <div>
              <h3 className={faqH3Style}>
                What plants grow well in small or shaded city gardens?
              </h3>
              <p
                className={articleFaqBodyStyle}
                style={{
                  color: vars.color.light3,
                  opacity: 0.8,
                }}
              >
                Shade-loving plants like ferns and hostas work well in darker courtyards,
                while sun-loving grasses, herbs, and compact shrubs thrive in brighter
                spaces. Climbers are excellent for all gardens because they make use of
                vertical surfaces such as walls and fences.
              </p>
            </div>
            <div>
              <h3 className={faqH3Style}>
                How do I maintain my garden if I don't have much time?
              </h3>
              <p
                className={articleFaqBodyStyle}
                style={{
                  color: vars.color.light3,
                  opacity: 0.8,
                }}
              >
                Keep your plant palette simple and repeat plants in larger groupings to
                reduce the number of species you need to care for. Choose low-maintenance
                shrubs, perennials, and hardy container plants. Automated or slow-release
                watering systems can also help.
              </p>
            </div>
          </div>
        </section>
        <div style={{ display: "flex", justifyContent: "center", marginTop: "1rem" }}>
          <LinkButton url="/hello/signup">Join Rescape AI</LinkButton>
        </div>
        <Footer showLinks={false} />
      </div>
    </>
  )
}

export default Index
